<template>
  <header class="dashboard-header">
    <!-- Left: Toggle & Breadcrumb -->
    <div class="dashboard-header__left">
      <button
        type="button"
        class="dashboard-header__icon-button dashboard-header__menu-toggle"
        aria-label="Buka atau tutup sidebar"
        @click="$emit('toggle-sidebar')"
      >
        <Icon icon="mdi:menu" width="22" height="22" />
      </button>

      <nav class="dashboard-header__breadcrumb" aria-label="Breadcrumb">
        <span class="dashboard-header__breadcrumb-parent">
          {{ sectionTitle }}
        </span>
        <Icon
          icon="mdi:chevron-right"
          width="16"
          height="16"
          class="dashboard-header__breadcrumb-separator"
        />
        <span class="dashboard-header__breadcrumb-current">
          {{ pageTitle }}
        </span>
      </nav>
    </div>

    <!-- Right: Actions -->
    <div class="dashboard-header__right">
      <!-- ✅ Notifications -->
      <div
        ref="notificationWrap"
        class="dashboard-header__notification-wrap"
      >
        <button
          type="button"
          class="dashboard-header__icon-button dashboard-header__notification"
          :aria-expanded="notificationsOpen"
          aria-haspopup="true"
          aria-label="Notifikasi"
          @click.stop="toggleNotifications"
        >
          <Icon icon="mdi:bell-outline" width="21" height="21" />
          <span
            v-if="unreadCount > 0"
            class="dashboard-header__notification-badge"
          >
            {{ unreadCount > 9 ? '9+' : unreadCount }}
          </span>
        </button>

        <Transition name="dropdown">
          <div
            v-if="notificationsOpen"
            class="notification-dropdown"
            role="menu"
            @click.stop
          >
            <div class="notification-dropdown__header">
              <div class="notification-dropdown__header-left">
                <h3 class="notification-dropdown__title">Notifikasi</h3>
                <span
                  v-if="unreadCount > 0"
                  class="notification-dropdown__count"
                >
                  {{ unreadCount }} baru
                </span>
              </div>
              <button
                v-if="unreadCount > 0"
                type="button"
                class="notification-dropdown__mark-read"
                @click.stop="handleMarkAllRead"
              >
                Tandai semua
              </button>
            </div>

            <div
              v-if="notifications.length"
              class="notification-dropdown__list"
            >
              <component
                :is="item.to ? 'router-link' : 'button'"
                v-for="item in notifications"
                :key="item.id"
                :to="item.to"
                :type="item.to ? undefined : 'button'"
                class="notification-item"
                :class="{
                  'is-unread': !item.read,
                  [`notification-item--${item.type || 'info'}`]: true
                }"
                @click="handleItemClick(item)"
              >
                <span class="notification-item__icon">
                  {{ item.icon || '🔔' }}
                </span>
                <div class="notification-item__body">
                  <p class="notification-item__title">{{ item.title }}</p>
                  <p
                    v-if="item.description"
                    class="notification-item__desc"
                  >
                    {{ item.description }}
                  </p>
                  <span
                    v-if="item.time"
                    class="notification-item__time"
                  >
                    {{ formatTime(item.time) }}
                  </span>
                </div>
                <span
                  v-if="!item.read"
                  class="notification-item__dot"
                  aria-hidden="true"
                ></span>
              </component>
            </div>

            <div v-else class="notification-dropdown__empty">
              <span class="notification-dropdown__empty-icon">🔔</span>
              <p class="notification-dropdown__empty-title">
                Belum ada notifikasi
              </p>
              <p class="notification-dropdown__empty-desc">
                Semua aman — tidak ada yang perlu diperhatikan.
              </p>
            </div>

            <div
              v-if="notifications.length"
              class="notification-dropdown__footer"
            >
              <button
                type="button"
                class="notification-dropdown__footer-link"
                @click.stop="handleViewAll"
              >
                Lihat semua notifikasi →
              </button>
            </div>
          </div>
        </Transition>
      </div>

      <!-- Profile -->
      <div
        class="dashboard-header__profile"
        v-click-outside="closeProfile"
      >
        <button
          type="button"
          class="dashboard-header__profile-button"
          :aria-expanded="profileOpen"
          aria-haspopup="true"
          aria-label="Menu profil pengguna"
          @click="toggleProfile"
        >
          <img
            v-if="resolvedAvatar && !avatarImageFailed"
            :key="resolvedAvatar"
            :src="resolvedAvatar"
            :alt="`Foto profil ${displayName}`"
            class="dashboard-header__avatar"
            @error="handleAvatarError"
          />
          <span
            v-else
            class="dashboard-header__avatar dashboard-header__avatar--fallback"
            aria-label="Inisial pengguna"
          >
            {{ initials }}
          </span>
          <span class="dashboard-header__profile-info">
            <span class="dashboard-header__profile-name">
              {{ displayName }}
            </span>
          </span>
          <Icon
            icon="mdi:chevron-down"
            width="18"
            height="18"
            class="dashboard-header__profile-chevron"
            :class="{ 'is-open': profileOpen }"
          />
        </button>

        <Transition name="dropdown">
          <div
            v-if="profileOpen"
            class="dashboard-header__dropdown"
            role="menu"
          >
            <button
              type="button"
              class="dashboard-header__dropdown-item"
              role="menuitem"
              @click="openProfile"
            >
              <Icon icon="mdi:account-outline" width="19" height="19" />
              <span>Profil Saya</span>
            </button>
            <button
              type="button"
              class="dashboard-header__dropdown-item dashboard-header__dropdown-item--logout"
              role="menuitem"
              @click="handleLogout"
            >
              <Icon icon="mdi:logout" width="19" height="19" />
              <span>Keluar</span>
            </button>
          </div>
        </Transition>
      </div>
    </div>
  </header>
</template>

<script>
import { Icon } from '@iconify/vue'
import { resolveAvatarUrl } from '@/services/axios'

export default {
  name: 'DashboardHeader',

  components: { Icon },

  props: {
    sectionTitle: { type: String, default: 'Dashboard' },
    pageTitle: { type: String, default: 'Ringkasan' },
    displayName: { type: String, default: 'Pengguna' },
    roleLabel: { type: String, default: 'Customer' },
    avatar: { type: String, default: '' },
    notifications: { type: Array, default: () => [] }
  },

  emits: [
    'toggle-sidebar',
    'open-notifications',
    'open-profile',
    'logout',
    'mark-all-read',
    'view-all-notifications',
    'mark-as-read' // ✅ BARU
  ],

  data() {
    return {
      profileOpen: false,
      notificationsOpen: false,
      avatarImageFailed: false
    }
  },

  computed: {
    resolvedAvatar() {
      return resolveAvatarUrl(this.avatar)
    },

    initials() {
      const name = (this.displayName || 'Pengguna').trim()
      return (
        name
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((part) => part.charAt(0).toUpperCase())
          .join('') || 'U'
      )
    },

    unreadCount() {
      return this.notifications.filter((n) => !n.read).length
    }
  },

  watch: {
    avatar() {
      this.avatarImageFailed = false
    }
  },

  methods: {
    toggleProfile() {
      this.profileOpen = !this.profileOpen
      if (this.profileOpen) this.notificationsOpen = false
    },

    closeProfile() {
      this.profileOpen = false
    },

    handleAvatarError() {
      this.avatarImageFailed = true
    },

    openProfile() {
      this.profileOpen = false
      this.$emit('open-profile')
    },

    handleLogout() {
      this.profileOpen = false
      this.$emit('logout')
    },

    toggleNotifications() {
      this.notificationsOpen = !this.notificationsOpen
      if (this.notificationsOpen) {
        this.profileOpen = false
        this.$emit('open-notifications')
      }
    },

    handleMarkAllRead() {
      this.$emit('mark-all-read')
    },

    handleViewAll() {
      this.notificationsOpen = false
      this.$emit('view-all-notifications')
    },

    /**
     * ✅ KUNCI: saat item diklik, emit mark-as-read.
     * Untuk welcome, ini akan set flag permanen di DB.
     */
    handleItemClick(item) {
      this.notificationsOpen = false

      if (!item.read) {
        this.$emit('mark-as-read', item.id)
      }

      if (!item.to) {
        this.$emit('open-notifications', item)
      }
    },

    formatTime(value) {
      if (!value) return ''
      const date = new Date(value)
      if (isNaN(date.getTime())) return ''

      const diffMs = Date.now() - date.getTime()
      const diffSec = Math.floor(diffMs / 1000)
      const diffMin = Math.floor(diffSec / 60)
      const diffHour = Math.floor(diffMin / 60)
      const diffDay = Math.floor(diffHour / 24)

      const rtf = new Intl.RelativeTimeFormat('id-ID', {
        numeric: 'auto'
      })

      if (diffSec < 60) return 'Baru saja'
      if (diffMin < 60) return rtf.format(-diffMin, 'minute')
      if (diffHour < 24) return rtf.format(-diffHour, 'hour')
      if (diffDay < 7) return rtf.format(-diffDay, 'day')

      return new Intl.DateTimeFormat('id-ID', {
        day: 'numeric',
        month: 'short'
      }).format(date)
    }
  },

  directives: {
    clickOutside: {
      mounted(el, binding) {
        el.__clickOutsideHandler__ = (event) => {
          if (!el.contains(event.target)) {
            binding.value(event)
          }
        }
        document.addEventListener('click', el.__clickOutsideHandler__)
      },
      unmounted(el) {
        document.removeEventListener('click', el.__clickOutsideHandler__)
        delete el.__clickOutsideHandler__
      }
    }
  }
}
</script>

<style scoped lang="scss">
.dashboard-header {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  padding: 0 24px;
  background: var(--color-background);
  color: var(--color-text);
  border-bottom: 1px solid var(--color-border);
  transition: background-color 0.2s ease, color 0.2s ease;

  &__left,
  &__right {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__left {
    min-width: 0;
    flex: 1;
  }

  &__icon-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;

    &:hover {
      background: var(--color-surface-hover);
      color: var(--color-text);
    }

    &:focus-visible {
      outline: 2px solid var(--color-button);
      outline-offset: 2px;
    }
  }

  &__menu-toggle {
    display: none;
    flex-shrink: 0;

    @media (max-width: 768px) {
      display: flex;
    }
  }

  &__breadcrumb {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    font-size: 14px;
  }

  &__breadcrumb-parent {
    max-width: 120px;
    overflow: hidden;
    color: var(--color-text-secondary);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__breadcrumb-separator {
    flex-shrink: 0;
    color: var(--color-text-secondary);
  }

  &__breadcrumb-current {
    max-width: 180px;
    overflow: hidden;
    color: var(--color-text);
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__notification-wrap {
    position: relative;
  }

  &__notification {
    position: relative;
  }

  &__notification-badge {
    position: absolute;
    top: 4px;
    right: 4px;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    border: 2px solid var(--color-surface);
    border-radius: 999px;
    background: #ef4444;
    color: #ffffff;
    font-size: 10px;
    font-weight: 700;
    line-height: 14px;
    text-align: center;
  }

  &__profile {
    position: relative;
  }

  &__profile-button {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 10px 6px 6px;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: var(--color-text);
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background: var(--color-surface-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--color-button);
      outline-offset: 2px;
    }
  }

  &__avatar {
    display: block;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    object-fit: cover;

    &--fallback {
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--color-button);
      color: #ffffff;
      font-size: 14px;
      font-weight: 600;
    }
  }

  &__profile-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.2;
    text-align: left;
  }

  &__profile-name {
    max-width: 140px;
    overflow: hidden;
    color: var(--color-text);
    font-size: 14px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__profile-chevron {
    flex-shrink: 0;
    color: var(--color-text-secondary);
    transition: transform 0.2s ease;

    &.is-open {
      transform: rotate(180deg);
    }
  }

  &__dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 50;
    min-width: 200px;
    padding: 6px;
    border: 1px solid var(--color-border);
    border-radius: 10px;
    background: var(--color-surface);
    color: var(--color-text);
    box-shadow: 0 8px 24px rgb(0 0 0 / 12%);
  }

  &__dropdown-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 12px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: var(--color-text);
    font-size: 14px;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;

    &:hover {
      background: var(--color-surface-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--color-button);
      outline-offset: -2px;
    }

    &--logout {
      color: #ef4444;

      &:hover {
        background: color-mix(in srgb, #ef4444 10%, var(--color-surface));
        color: #ef4444;
      }
    }
  }
}

/* Dropdown notifikasi */
.notification-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 50;
  width: 380px;
  max-width: calc(100vw - 32px);
  background: var(--color-surface);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgb(0 0 0 / 14%);
  overflow: hidden;
}

.notification-dropdown__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--color-border);
}

.notification-dropdown__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.notification-dropdown__title {
  margin: 0;
  color: var(--color-text);
  font-size: 14px;
  font-weight: 700;
}

.notification-dropdown__count {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: color-mix(in srgb, #ef4444 14%, var(--color-surface));
  color: #ef4444;
  font-size: 10.5px;
  font-weight: 700;
  white-space: nowrap;
}

.notification-dropdown__mark-read {
  flex-shrink: 0;
  padding: 4px 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--color-button);
  font: inherit;
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--color-surface-hover);
  }

  &:focus-visible {
    outline: 2px solid var(--color-button);
    outline-offset: 2px;
  }
}

.notification-dropdown__list {
  max-height: 380px;
  overflow-y: auto;
  padding: 4px;
}

.notification-dropdown__list::-webkit-scrollbar {
  width: 6px;
}

.notification-dropdown__list::-webkit-scrollbar-thumb {
  background: var(--color-border);
  border-radius: 3px;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--color-text);
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--color-surface-hover);
  }

  &.is-unread {
    background: color-mix(in srgb, var(--color-button) 7%, var(--color-surface));
  }

  &:focus-visible {
    outline: 2px solid var(--color-button);
    outline-offset: -2px;
  }

  &__icon {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 9px;
    font-size: 17px;
    background: var(--color-surface-hover);
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__title {
    margin: 0;
    color: var(--color-text);
    font-size: 13px;
    font-weight: 600;
    line-height: 1.4;
    overflow-wrap: anywhere;
  }

  &__desc {
    margin: 3px 0 0;
    color: var(--color-text-secondary);
    font-size: 11.5px;
    line-height: 1.45;
    overflow-wrap: anywhere;
  }

  &__time {
    display: inline-block;
    margin-top: 4px;
    color: var(--color-text-secondary);
    font-size: 10.5px;
    font-weight: 500;
  }

  &__dot {
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    margin-top: 6px;
    border-radius: 50%;
    background: var(--color-button);
  }

  &--warning .notification-item__icon {
    background: color-mix(in srgb, #f59e0b 14%, var(--color-surface));
    color: #f59e0b;
  }

  &--danger .notification-item__icon {
    background: color-mix(in srgb, #ef4444 12%, var(--color-surface));
    color: #ef4444;
  }

  &--info .notification-item__icon {
    background: color-mix(in srgb, var(--color-button) 12%, var(--color-surface));
    color: var(--color-button);
  }

  &--success .notification-item__icon {
    background: color-mix(in srgb, #10b981 12%, var(--color-surface));
    color: #10b981;
  }
}

.notification-dropdown__empty {
  padding: 32px 20px;
  text-align: center;
}

.notification-dropdown__empty-icon {
  display: block;
  margin-bottom: 8px;
  font-size: 32px;
  opacity: 0.55;
}

.notification-dropdown__empty-title {
  margin: 0 0 4px;
  color: var(--color-text);
  font-size: 13px;
  font-weight: 600;
}

.notification-dropdown__empty-desc {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 11.5px;
  line-height: 1.45;
}

.notification-dropdown__footer {
  padding: 8px;
  border-top: 1px solid var(--color-border);
  background: var(--color-background-secondary);
}

.notification-dropdown__footer-link {
  display: block;
  width: 100%;
  padding: 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--color-button);
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--color-surface-hover);
  }

  &:focus-visible {
    outline: 2px solid var(--color-button);
    outline-offset: -2px;
  }
}

/* Animasi dropdown */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Responsif */
@media (max-width: 768px) {
  .dashboard-header {
    padding: 0 16px;

    &__breadcrumb-parent,
    &__breadcrumb-separator {
      display: none;
    }

    &__profile-info,
    &__profile-chevron {
      display: none;
    }

    &__profile-button {
      padding: 4px;
    }
  }

  .notification-dropdown {
    width: 320px;
    max-width: calc(100vw - 24px);
  }

  .notification-dropdown__list {
    max-height: 300px;
  }
}

/* Kurangi animasi jika diminta sistem */
@media (prefers-reduced-motion: reduce) {
  .dashboard-header *,
  .notification-item,
  .notification-dropdown__mark-read,
  .notification-dropdown__footer-link,
  .dropdown-enter-active,
  .dropdown-leave-active {
    transition: none !important;
  }
}
</style>
